import type { ComponentPropsWithRef, ReactNode } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type AlertBannerVariant = "success" | "warning" | "error" | "info";

export const ALERT_BANNER_SURFACE_CLASS: Record<AlertBannerVariant, string> = {
  success: "border border-success-600/20 bg-success-50/80 text-success-900",
  warning: "border border-warning-600/25 bg-warning-50/80 text-warning-900",
  error: "border border-error-600/20 bg-error-50/80 text-error-900",
  info: "border border-primary-600/20 bg-primary-50/80 text-primary-900",
};

const VARIANT_CLASS: Record<
  AlertBannerVariant,
  { wrap: string; icon: string; title: string; body: string }
> = {
  success: {
    wrap: ALERT_BANNER_SURFACE_CLASS.success,
    icon: "text-success-600",
    title: "text-success-900",
    body: "text-success-700",
  },
  warning: {
    wrap: ALERT_BANNER_SURFACE_CLASS.warning,
    icon: "text-warning-600",
    title: "text-warning-900",
    body: "text-warning-700",
  },
  error: {
    wrap: ALERT_BANNER_SURFACE_CLASS.error,
    icon: "text-error-600",
    title: "text-error-900",
    body: "text-error-700",
  },
  info: {
    wrap: ALERT_BANNER_SURFACE_CLASS.info,
    icon: "text-primary-600",
    title: "text-primary-900",
    body: "text-primary-700",
  },
};

const VARIANT_ICON: Record<AlertBannerVariant, LucideIcon> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

export type AlertProps = Omit<ComponentPropsWithRef<"div">, "children"> & {
  variant?: AlertBannerVariant;
  title?: string;
  children?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
};

export function Alert({
  variant = "info",
  title,
  children,
  icon,
  action,
  className,
  ...rest
}: AlertProps) {
  const styles = VARIANT_CLASS[variant];
  const Icon = icon ?? VARIANT_ICON[variant];

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg px-3.5 py-3 text-sm",
        styles.wrap,
        className,
      )}
      {...rest}
      role={rest.role ?? "status"}
    >
      <Icon
        className={cn("mt-px h-[18px] w-[18px] shrink-0", styles.icon)}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        {title ? (
          <p className={cn("font-medium", styles.title)}>{title}</p>
        ) : null}
        {children ? (
          <div className={cn(title ? "mt-0.5" : undefined, styles.body)}>
            {children}
          </div>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
